import DesignTokens from '@tableau/design-tokens';
import {
  ButtonType,
  ButtonWidget,
  DialogFooterButtonsWidget,
  DialogPositioningBehavior,
  DialogProps,
  DialogType,
  DialogWidget,
  TabStyles,
  TextFieldProps,
  TextFieldWidget,
  TextLinkKind,
  TextLinkWidget
} from '@tableau/widgets';
import * as clipboard from 'clipboard-polyfill';
import React from 'react';
import {style} from 'typestyle';

import {FeatureFlags} from '~commonInfra/featureFlags/FeatureFlags';
import {imgPath} from '~commonInfra/utils/ImgUrl';
import { internalTextFieldStyle, textWidgetStyle } from '~src/authentication/styles/CreatePersonalAccessTokenDialogStyle';
import { SessionServerSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '~src/codegen/Localize';
import { ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import {IResult} from '~src/httpClient/serverApi/types/IResponse';
import { SettingsSection } from '~src/settings/components/common/SettingsSection';
import { useSettingsCoordinator } from '~src/settings/components/settings-coordinator/SettingsCoordinatorHook';
import {
  ContentMigrationAuthorizationApi,
  ContentMigrationPublicKeysInfo
} from '~src/settings/general-settings/components/sections/content-migration/ContentMigrationAuthorizationApi';
import { useSelector } from '~src/settings/general-settings/utils';
import { learnMoreStyle } from '~src/settings/styles/CommonStyles';
import { applyColor }from '~src/style/ColorService';

import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';



function processError(result: IResult, message: string) {
  ToasterService.instance.pop({ type: ToastType.Error, text: message });
}


export function ContentMigrationAuthorizationSection() {
  const isOnline = useSelector(SessionServerSelectors.isOnlineEnvironment);
  const handleSaveAction = React.useCallback(async (values: 1, formik: 1) => {}, []);
  const [publicKeys, setPublicKeys] = React.useState([] as ContentMigrationPublicKeysInfo[]);

  const initialValues = React.useMemo(() => {
    return publicKeys;
  }, [publicKeys]);

  const refreshPublicKeys = React.useCallback(async () => {
    const publicKeys = await ContentMigrationAuthorizationApi.instance.getExistingContentMigrationPublicKeys();
    setPublicKeys(publicKeys);
  }, []);

  const createNewEncryptionKeys = React.useCallback(async () => {
    const result = await ContentMigrationAuthorizationApi.instance.generateDestinationCredentialMigrationEncryptionKeys();
    openNewTokenDialog({
      id: result.id,
      publicKey: result.publicKey,
      expirationTime: result.expirationTime,
      migrationLuid: result.migrationLuid,
      onClose: null,
      discardFunction: (publicKey: string) => deleteEncryptionKey(publicKey)
    });
    setPublicKeys(await ContentMigrationAuthorizationApi.instance.getExistingContentMigrationPublicKeys());
    // eslint-disable-next-line
  }, []);

  const deleteEncryptionKey = React.useCallback(async (publicKey: string) => {
    const result = await ContentMigrationAuthorizationApi.instance.deleteContentMigrationPublicKey({migrationLuid: publicKey});
    if (result && !result.errors) {
      ToasterService.instance.pop({ type: ToastType.Success, text: 'Deleted migration and keys' });
      setPublicKeys(await ContentMigrationAuthorizationApi.instance.getExistingContentMigrationPublicKeys());
    } else {
      ToasterService.instance.pop({ type: ToastType.Error, text: 'Unable to delete migration' });
    }
  }, []);

  React.useEffect(() => {
    setPublicKeys([]);
    refreshPublicKeys();
    // eslint-disable-next-line
  }, []);

  if (!FeatureFlags.embeddedCredentialMigrationEnabled || !isOnline) {
    return null;
  }

  return (
    <SettingsSection title={Messages.contentMigration_settingTitle()}
      description={getDescription()}
      testId='content-migration-authorization-site-setting'>
      <div>
        <FormContent
          publicKeys={publicKeys}
          createNewPublicKey={() => createNewEncryptionKeys()}
          deletePublicKey={(publicKey: string) => deleteEncryptionKey(publicKey)}
          refreshPublicKeys={() => refreshPublicKeys()}/>
      </div>
    </SettingsSection>
  );
}

function getDescription() {
  return <>
    <span data-tb-test-id='content-migration-keys-description'>
      {Messages.contentMigration_description()}
      <TextLinkWidget
        testId='content-migration-keys-learn-more'
        className={learnMoreStyle}
        kind={TextLinkKind.Standard}
        href={HelpService.getHelpUrlByCategory('cmt_migration_keys')}
        target={'_blank'}
        rel={'noreferrer noopener'}
      >
        {Messages.learnMore()}
      </TextLinkWidget>
    </span>
  </>;
}

interface FormContentProps{
  publicKeys: ContentMigrationPublicKeysInfo[];
  createNewPublicKey: () => void;
  deletePublicKey: (migrationLuid: string) => void;
  refreshPublicKeys: () => void;
}

function FormContent(props: FormContentProps) {
  useSettingsCoordinator();

  const isOnline = useSelector(SessionServerSelectors.isOnlineEnvironment);

  return <>
    <div>
      <ButtonWidget buttonType={ButtonType.Outline}
        testId='create-content-migration-keys-button'
        className='tb-outline-button tb-margin-left-wide'
        onClick={() => props.createNewPublicKey()}
        disabled={false}
        type='button'>{Messages.contentMigration_createNewKey()}
      </ButtonWidget>
    </div>
    <p />
    <div>
      {props.publicKeys.length > 0 &&
      <table id='content-migration-public-keys-list-table-id' className='tb-static-grid-table tb-static-grid-table-settings-width'>
        <thead>
          <tr data-test-id='create-content-migration-public-key-class'>
            <th className='tb-data-grid-headers-line'>
              <span className='tb-data-grid-header-text'>{Messages.contentMigration_publicKey()}</span>
            </th>
            <th className='tb-data-grid-headers-line'>
              <span className='tb-data-grid-header-text'>{Messages.contentMigration_expires()}</span>
            </th>
            <th className='tb-data-grid-headers-line'>
              <span className='tb-data-grid-header-text'>{Messages.actions()}</span>
            </th>
          </tr>
        </thead>
        <ListContentMigrationPublicKeysSection publicKeys={props.publicKeys} isOnline={isOnline} refreshPublicKeys={props.refreshPublicKeys} deletePublicKey={props.deletePublicKey}/>
      </table>}
     </div>
  </>;
}

interface ListContentMigrationPublicKeysSectionProps {
  publicKeys: ContentMigrationPublicKeysInfo[];
  isOnline: boolean;
  deletePublicKey: (publicKey: string) => void;
  refreshPublicKeys: () => void;
}

export function ListContentMigrationPublicKeysSection(props: ListContentMigrationPublicKeysSectionProps) {
  return (
    <tbody>
    {props.publicKeys.map((publicKeyInfo) => {
      return (
        <tr key={publicKeyInfo.publicKey} data-tb-test-id={'content-migration-keys-list-item-' + publicKeyInfo.migrationLuid}>
          <td>
            <div>
              <span title={publicKeyInfo.publicKey} style={{marginLeft: '0.8rem'}}>
                {publicKeyInfo.publicKey}
              </span>
            </div>
          </td>
          <td>
            <div>
              <span title={publicKeyInfo.expirationTime} style={{marginLeft: '0.8rem'}}>
                {publicKeyInfo.expirationTime.substring(0,16)}
              </span>
            </div>
          </td>
          <td>
            <ButtonWidget
              title={Messages.contentMigration_delete()}
              buttonType={ButtonType.LowEmphasis}
              style={{ float: 'right' }}
              onClick={() => props.deletePublicKey(publicKeyInfo.migrationLuid)}
            >
              <img src={imgPath('trash_14px_small.svg')} alt="" /><span>{' ' + Messages.contentMigration_delete()}</span>
            </ButtonWidget>
          </td>
        </tr>
      );
    })}
    </tbody>
  );
}


type CreateContentMigrationPublicKeyDialogProps = {
  publicKeyInfo: ContentMigrationPublicKeysInfo;
  discardFunction: (publicKey: string) => void;
  dismiss: (reason?: any) => void;
};


export function openNewTokenDialog(params: {
  id: number;
  publicKey: string;
  expirationTime: string;
  migrationLuid: string;
  onClose: () => void;
  discardFunction: (publicKey: string) => void;
}){
  const publicKeyInfo: ContentMigrationPublicKeysInfo = {
    authorizedMigrationRunner: '',
    isDestination: true,
    id: params.id,
    publicKey: params.publicKey,
    expirationTime: params.expirationTime,
    migrationLuid: params.migrationLuid
  };

  ReactModalService.openCustom(closeAndDismiss => {
    const props: CreateContentMigrationPublicKeyDialogProps = {
      publicKeyInfo: publicKeyInfo,
      discardFunction: params.discardFunction,
      dismiss: closeAndDismiss.dismiss
    };
    const dialogProps = ReactModalService.getDefaultDialogProps({
      type: DialogType.NormalWithTitleBar,
      title: '  ',
      testId: 'content-migration-public-key-created-dialog',
      showCloseButton: true,
      positioningBehavior: DialogPositioningBehavior.Centered,
      dialogWidth: TabDialogWidth.width500px,
      footerContent: GetFooterContent(props)
    }, closeAndDismiss);

    return GetDialogWidget(dialogProps, publicKeyInfo);
  }).result.finally(params.onClose);
}

const InternalTextField: React.FC<Partial<TextFieldProps> & React.HTMLAttributes<HTMLInputElement>> = props => {
  return <TextFieldWidget
    {...props}
    label={props.label || props.kind}
    style={internalTextFieldStyle}
    testId={props.testId}
    disabled={true}
  />;
};
const successStyle: string = style(
  {
    color: applyColor(DesignTokens.color.component.content.neutral.default.default, TabStyles.Colors.Black), textAlign: 'left', fontSize: '20px', fontWeight: 'normal'
  }
);

function GetDialogWidget(dialogProps: DialogProps, publicKeyInfo: ContentMigrationPublicKeysInfo) {
  return (
    <DialogWidget {...dialogProps}>
      <div className={successStyle}>
        {Messages.contentMigration_creationSuccess()}
      </div>
      <p/>
      <div className={textWidgetStyle}>
        <InternalTextField kind='outline' label={Messages.contentMigration_publicKeyCreatedDescription()} testId='create-personal-access-token-token-name' defaultValue={publicKeyInfo.publicKey}/>
        <InternalTextField kind='outline' label={Messages.contentMigration_expires()} testId='create-personal-access-token-token-value' defaultValue={publicKeyInfo.expirationTime}/>
      </div>
    </DialogWidget>
  );
}

function copyToClipboard(props: CreateContentMigrationPublicKeyDialogProps) {
  clipboard.writeText(props.publicKeyInfo.publicKey);
  ToasterService.instance.pop({ type: ToastType.Info, text: Messages.contentMigration_copied()});
}

export const ContentMigrationAuthorizationSectionSpecs = componentRegistryEntry({
  name: 'ContentMigrationAuthorizationSection',
  component: ContentMigrationAuthorizationSection
});

function GetFooterContent(props: CreateContentMigrationPublicKeyDialogProps): React.ReactNode {
  return (
    <>
    <DialogFooterButtonsWidget
      buttons={[
        {
          handleClick: () => {
            props.discardFunction(props.publicKeyInfo.migrationLuid);
            props.dismiss();
          },
          buttonType: ButtonType.Outline,
          children: Messages.contentMigration_discard(),
          testId: 'footer-discard',
        },
        {
          handleClick: () => {
            copyToClipboard(props);
          },
          buttonType: ButtonType.Primary,
          children: Messages.contentMigration_copyToClipboard(),
          testId: 'footer-copy',
        }
      ]}
      testId='footer'
    />
    </>
  );
}
